import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, ButtonBase, FormControl, FormControlLabel, MenuItem, Radio, RadioGroup } from '@mui/material';
import i18next, { t } from 'i18next';
import validator from 'validator';

import { authenticationAPIService } from '~/api/login';
import DeleteImage from '~/assets/icons/modal/delete.svg?react';
import MailImage from '~/assets/icons/modal/mail.svg?react';
import PersonIcon from '~/assets/icons/svgs/forms/person.svg?react';
import PhoneIcon from '~/assets/icons/svgs/forms/phone.svg?react';
import MailIcon from '~/assets/icons/svgs/mail.svg?react';
import ContentContainer from '~/components/atoms/content-container/ContentContainer';
import { resetFullQrState } from '~/components/organisms/previewBar/utils';
import { ConfirmModal } from '~/components/ui/ConfirmModal/ConfirmModal';
import { Text } from '~/components/ui/text/Text';
import TextInput from '~/components/ui/TextInput';
import { separators } from '~/constants/common';
import { language } from '~/constants/language';
import { TIME_ZONE_FORMAT } from '~/constants/time-zone';
import { useAppDispatch } from '~/hooks/useRedux';
import { googleAnalytics } from '~/services/google-analytics-service';
import { getLoggedInUser } from '~/store/reducers/account/selector';
import { getSubscriptionInfoThunk, updateAccountThunk } from '~/store/reducers/account/slice';
import { COLORS } from '~/styles/colors';
import { extractFirstNumbers, logOut } from '~/utils/common';
import { getCurrentUTCOffset, isMaxLength } from '~/utils/common';

import * as S from '../styles';

import { UpdateButton } from './UpdateButton';

type GeneralInformationProps = {
  data: any;
};

function GeneralInformation({ data }: GeneralInformationProps) {
  const dispatch = useAppDispatch();
  const [timeLabel, setTimeLable] = useState(data.dateFormat);
  const [hoursFormat, setHoursFormat] = useState(+extractFirstNumbers(data.hoursFormat));
  const initialContactInfo = [
    {
      label: t('settings:name'),
      icon: <PersonIcon />,
      value: data.firstName || '',
      filedName: 'firstName',
      placeholder: 'E.g. John',
    },
    {
      label: t('settings:surname'),
      icon: <PersonIcon />,
      value: data.lastName || '',
      filedName: 'lastName',
      placeholder: 'E.g. Doe',
    },
    {
      label: t('settings:email'),
      icon: <MailIcon color={COLORS.primary} />,
      value: data.email || '',
      filedName: 'email',
      placeholder: 'molac56416@easipro.com',
    },
    {
      label: t('settings:phone'),
      icon: <PhoneIcon color={COLORS.primary} />,
      value: data.phone,
      filedName: 'phone',
      placeholder: 'E.g. 655221346',
    },
  ];

  const { data: dataAccount, error, loading, updated } = useSelector(getLoggedInUser);

  const textInputRef = useRef(null);
  const [currentLanguage, setLanguage] = useState(data.languageCode);
  const [currentSeparators, setSeparators] = useState(data.thousandsSeparator);
  const [currentTimeZone, setTimeZone] = useState((data.timezone !== 'UTC' && data.timezone) || getCurrentUTCOffset());
  const [contactInfo, setContactInfo] = useState(initialContactInfo);
  const [isChangedContact, setIsChangedContact] = useState(false);
  const [isChangedLanguage, setChangeLanguage] = useState(false);
  const [isChangedSeparators, setChangeSeparators] = useState(false);
  const [isChangedTimeZone, setChangeTimeZone] = useState(false);
  const [isChangedFormat, setChangeFormat] = useState(false);
  const [isContactInfoValid, setIsContactInfoValid] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [isUpdated, setIsUpdated] = useState({
    contact: false,
    language: false,
    time: false,
    date: false,
    thousands: false,
  });

  const [isOpenSubscriptionModal, setIsOpenSubscriptionModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  useEffect(() => {
    const hasChanges = contactInfo.some((item, index) => item.value !== initialContactInfo[index].value);
    setIsChangedContact(hasChanges);
  }, [contactInfo, data]);

  const handleInputChange = (index: number, newValue) => {
    setContactInfo((prevContactInfo) => {
      const updatedContactInfo = [...prevContactInfo];
      updatedContactInfo[index] = { ...updatedContactInfo[index], value: newValue };
      return updatedContactInfo;
    });

    setIsUpdated((prev) => ({ ...prev, contact: !prev }));
  };

  const handleLanguageChange = (e) => {
    const value = e.target.value;

    setLanguage((prewLang) => {
      if (prewLang !== value) {
        setChangeLanguage(true);
      }
      return value;
    });

    setIsUpdated((prev) => ({ ...prev, language: !prev }));
  };

  const handleSeparatorsChange = (e) => {
    const value = e.target.value;

    setSeparators((prew) => {
      if (prew !== value) {
        setChangeSeparators(true);
      }
      return value;
    });
    setIsUpdated((prev) => ({ ...prev, thousands: !prev }));
  };

  const handleTimeZoneChange = (e) => {
    setTimeZone((prewLang) => {
      if (prewLang !== e.target.value) {
        setChangeTimeZone(true);
      }
      return e.target.value;
    });

    setIsUpdated((prev) => ({ ...prev, time: !prev }));
  };

  const onSaveContactInfo = () => {
    if (isChangedContact) {
      const data = contactInfo.reduce((acc, item) => {
        if (item.filedName === 'phone') {
          acc[item.filedName] = item.value;
        } else {
          acc[item.filedName] = item.value;
        }
        return acc;
      }, {});
      dispatch(updateAccountThunk({ ...data }));
      setIsUpdated((prev) => ({ ...prev, contact: true }));
    }
  };

  const onSaveLanguage = async () => {
    await dispatch(updateAccountThunk({ languageCode: currentLanguage }));
    setChangeLanguage(false);

    localStorage.setItem('language', currentLanguage);
    i18next.changeLanguage(currentLanguage);
    setIsUpdated((prev) => ({ ...prev, language: true }));
  };

  const onSaveSeparators = async () => {
    await dispatch(updateAccountThunk({ thousandsSeparator: currentSeparators }));
    setChangeSeparators(false);
    setIsUpdated((prev) => ({ ...prev, thousands: true }));
  };

  const onSaveTimeZone = () => {
    dispatch(updateAccountThunk({ timezone: currentTimeZone }));
    setChangeTimeZone(false);
    setIsUpdated((prev) => ({ ...prev, time: true }));
  };

  const onSaveTimeFormat = async () => {
    await dispatch(updateAccountThunk({ date_format: timeLabel, hours_format: `${hoursFormat}_hours` }));
    dispatch(getSubscriptionInfoThunk());
    setChangeFormat(false);
    setIsUpdated((prev) => ({ ...prev, date: true }));
  };

  const handleChangeTimeLable = (e) => {
    setTimeLable((prewLang) => {
      if (prewLang !== e.target.value) {
        setChangeFormat(true);
      }
      return e.target.value;
    });
    setIsUpdated((prev) => ({ ...prev, date: !prev }));
  };

  const handleChangeHoursFormat = (e) => {
    setHoursFormat((prewLang) => {
      if (prewLang !== e.target.value) {
        setChangeFormat(true);
      }
      return e.target.value;
    });

    setIsUpdated((prev) => ({ ...prev, date: !prev }));
  };

  const getMaxLength = (fieldName) => {
    if (fieldName === 'email') return 254;
    if (fieldName === 'phone') return 30;
    return 70;
  };

  const isContactInfoValidForm = () => {
    const emailField = contactInfo.find((item) => item?.filedName === 'email')?.value;
    const phoneField = contactInfo.find((item) => item?.filedName === 'phone')?.value;

    const isEmailValid = emailField && validator.isEmail(emailField);
    const isPhoneValid = phoneField && phoneField.trim() !== '' && validator.isMobilePhone(phoneField);

    const hasCodeError = phoneField?.trim() === '';

    setPhoneNumberError(hasCodeError ? t('errors:phoneIsValid') : '');

    const isPhoneValidCombined = phoneField ? isPhoneValid : true;

    return isEmailValid && isPhoneValidCombined;
  };

  // some bug in here if we remove get infinite rerender
  useEffect(() => {
    const valid = isContactInfoValidForm();
    setIsContactInfoValid(valid);
  }, [contactInfo]);

  const emailValidation = (filed, value) => {
    if (filed === 'email') {
      return !validator.isEmail(value);
    }
  };

  const handleLogOutClick = async () => {
    await logOut();
    resetFullQrState();
    googleAnalytics.events.logOut();
  };

  return (
    <Box>
      <Box sx={S.generalContainer}>
        {/* Contact information section */}
        <ContentContainer containerStyles={{ ...S.formContainer, gridArea: 'contact' }}>
          <Text variant="h3">{t('settings:titleSection1')}</Text>
          <Box sx={S.inputsContainer}>
            {contactInfo.map((item, index) => {
              return (
                <Box key={item.label}>
                  <TextInput
                    fullWidth
                    icon={item.icon}
                    label={item.label}
                    defaultValue={item.value}
                    value={item.value}
                    placeholder={item.placeholder}
                    error={
                      emailValidation(item.filedName, item.value) ||
                      (item.filedName === 'phone' && !!phoneNumberError) ||
                      (item.filedName === 'email' && error && !!error) ||
                      (item.filedName === 'phone' && item.value && !validator.isMobilePhone(item.value))
                    }
                    helperText={
                      (emailValidation(item.filedName, item.value) && t('errors:emailIsValid')) ||
                      (item.filedName === 'email' && error && error?.email[0]) ||
                      isMaxLength(item.value, getMaxLength(item?.filedName)) ||
                      (item.filedName === 'phone' &&
                        (item.value && !validator.isMobilePhone(item.value)
                          ? t('errors:phoneIsValid')
                          : phoneNumberError)) ||
                      undefined
                    }
                    inputProps={{ maxLength: getMaxLength(item?.filedName) }}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                  />
                </Box>
              );
            })}
          </Box>
          <UpdateButton
            isLoader={loading && isChangedContact}
            isDisbled={!isChangedContact || !isContactInfoValid}
            isUpdated={updated && isUpdated.contact}
            text={t('settings:informationUpdated')}
            onClick={() => onSaveContactInfo()}
          />
        </ContentContainer>

        {/* Language section */}
        <ContentContainer containerStyles={{ ...S.formContainer, gridArea: 'language' }}>
          <Text variant="h3">{t('settings:Language')}</Text>
          <Box sx={S.selectContainer}>
            <TextInput
              label={t('settings:Language')}
              fullWidth
              ref={textInputRef}
              select
              defaultValue={currentLanguage || 'en'}
              onChange={handleLanguageChange}
            >
              {language.map((option) => (
                <MenuItem key={option.value} value={option.value} defaultValue={'en'}>
                  {option.label}
                </MenuItem>
              ))}
            </TextInput>
          </Box>

          <UpdateButton
            isLoader={loading && isChangedLanguage ? 'loading' : ''}
            isDisbled={!isChangedLanguage}
            isUpdated={updated && isUpdated.language}
            text={t('settings:languageUpdated')}
            onClick={() => onSaveLanguage()}
          />
        </ContentContainer>

        {/* Time Zone first section */}
        <ContentContainer containerStyles={{ ...S.formContainer, gridArea: 'time' }}>
          <Text variant="h3">{t('settings:titleSection3')}</Text>
          <Box sx={S.selectContainer}>
            <TextInput
              label={t('settings:titleSection3')}
              fullWidth
              SelectProps={{
                MenuProps: {
                  sx: { height: '50%' },
                },
              }}
              select
              defaultValue={currentTimeZone || ''}
              onChange={handleTimeZoneChange}
            >
              {TIME_ZONE_FORMAT.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextInput>
          </Box>
          <UpdateButton
            isDisbled={!isChangedTimeZone}
            isUpdated={updated && isUpdated.time}
            text={t('settings:timeUpdated')}
            onClick={() => onSaveTimeZone()}
          />
        </ContentContainer>

        {/* Date second section */}
        <ContentContainer containerStyles={{ ...S.formContainer, gridArea: 'date' }}>
          <Text variant="h3">{t('settings:titleSection5')}</Text>
          <Box sx={S.radioContainer}>
            <FormControl>
              <Text variant="inputSmall" sx={{ color: 'var(--grey400)', marginBottom: '12px' }}>
                {t('settings:dateFormat')}
              </Text>
              <RadioGroup value={timeLabel} onChange={handleChangeTimeLable}>
                <FormControlLabel
                  sx={S.radioControlLabel}
                  value="DD-MM-YYYY"
                  control={<Radio disableRipple />}
                  label="DD-MM-YYYY"
                />
                <FormControlLabel
                  sx={S.radioControlLabel}
                  value="MM-DD-YYYY"
                  control={<Radio disableRipple />}
                  label="MM-DD-YYYY"
                />
              </RadioGroup>
            </FormControl>
            <FormControl>
              <Text variant="inputSmall" sx={{ color: 'var(--grey400)', marginBottom: '12px' }}>
                {t('settings:hoursFormat')}
              </Text>
              <RadioGroup value={hoursFormat} onChange={handleChangeHoursFormat}>
                <FormControlLabel
                  sx={S.radioControlLabel}
                  value={24}
                  control={<Radio disableRipple />}
                  label="24 hours"
                />
                <FormControlLabel
                  sx={S.radioControlLabel}
                  value={12}
                  control={<Radio disableRipple />}
                  label="12 hours"
                />
              </RadioGroup>
            </FormControl>
          </Box>
          <UpdateButton
            isDisbled={!isChangedFormat}
            isUpdated={updated && isUpdated.date}
            text={t('settings:dateUpdated')}
            onClick={() => onSaveTimeFormat()}
          />
        </ContentContainer>

        {/*Thousands*/}
        <ContentContainer containerStyles={{ ...S.formContainer, gridArea: 'thousands' }}>
          <Text variant="h3">{t('settings:titleSection6')}</Text>
          <Box sx={[S.selectContainer, { marginBottom: '40px' }]}>
            <TextInput
              label={t('settings:titleSection6')}
              fullWidth
              ref={textInputRef}
              select
              defaultValue={currentSeparators === null ? '.' : currentSeparators}
              onChange={handleSeparatorsChange}
            >
              {separators.map((option) => (
                <MenuItem key={option.value} value={option.value} defaultValue={'.'}>
                  {option.label}
                </MenuItem>
              ))}
            </TextInput>
          </Box>
          <UpdateButton
            isDisbled={!isChangedSeparators}
            isUpdated={updated && isUpdated.thousands}
            text={t('settings:thousandsUpdated')}
            onClick={() => onSaveSeparators()}
          />
        </ContentContainer>

        {/* Account details section */}
        <ContentContainer containerStyles={{ ...S.accoutnDetailsContainer, gridArea: 'details' }}>
          <Text variant="h3">{t('settings:titleSection4')}</Text>
          <Box sx={S.detailsBox}>
            <Box sx={[S.detailsItem, { marginBottom: '16px' }]}>
              <Text variant="inputSmall">{t('settings:subscribeTitle')}</Text>
              <Text variant="inputSmall" sx={{ color: 'var(--grey400)' }}>
                {dataAccount.isNotificationsEnabled ? t('settings:subscribeDesc') : t('settings:subscribeDesc2')}
              </Text>
              <ButtonBase sx={S.updateBtn} className="subscribe" onClick={() => setIsOpenSubscriptionModal(true)}>
                {dataAccount.isNotificationsEnabled ? t('settings:unsubBtn') : t('settings:subBtn')}
              </ButtonBase>
            </Box>
            {/*<Box sx={S.detailsItem}>*/}
            {/*  <Text variant="inputSmall">{t('settings:accTitle')}</Text>*/}
            {/*  <Text variant="inputSmall" sx={{ color: 'var(--grey400)' }}>*/}
            {/*    {t('settings:accDesc')}*/}
            {/*  </Text>*/}
            {/*  <ButtonBase sx={S.updateBtn} className="delete" onClick={() => setIsOpenDeleteModal(true)}>*/}
            {/*    {t('settings:deleteAccBtn')}*/}
            {/*  </ButtonBase>*/}
            {/*</Box>*/}
          </Box>
        </ContentContainer>
      </Box>
      <ButtonBase
        sx={{
          background: COLORS.darkRed,
          borderRadius: '20px',
          padding: '4px 24px',
          fontSize: '18px',
          fontWeight: 500,
          color: COLORS.white,
          height: '33px',
        }}
        className="subscribe"
        onClick={handleLogOutClick}
      >
        {t('buttons:logOutBtn')}
      </ButtonBase>

      <ConfirmModal
        onClose={() => setIsOpenDeleteModal(false)}
        open={isOpenDeleteModal}
        confirmText={t('buttons:deletePermanently')}
        isDelateStyle={true}
        onConfirm={async () => {
          await authenticationAPIService.deleteAccount();
          await logOut();
        }}
      >
        <DeleteImage />
        <Text variant="h2" sx={{ marginTop: '32px' }}>
          {t('popup:accountDelete')}
        </Text>
        <Text sx={{ display: 'block', color: 'var(--grey400)' }} component="span">
          {t('popup:accountDeleteSubtitle')}
        </Text>
      </ConfirmModal>

      <ConfirmModal
        onClose={() => setIsOpenSubscriptionModal(false)}
        open={isOpenSubscriptionModal}
        confirmText={dataAccount.isNotificationsEnabled ? t('popup:emailUnsubscribe') : t('popup:emailSubscribe')}
        isDelateStyle={dataAccount.isNotificationsEnabled}
        onConfirm={() => {
          dispatch(updateAccountThunk({ isNotificationsEnabled: !dataAccount.isNotificationsEnabled }));
          setIsOpenSubscriptionModal(false);
        }}
      >
        <MailImage />
        <Text variant="h2" sx={{ marginTop: '32px' }}>
          {dataAccount.isNotificationsEnabled ? t('popup:emailUnsubscribe') : t('popup:emailSubscribe')}
        </Text>
        <Text sx={{ display: 'block', color: 'var(--grey400)' }} component="span">
          {dataAccount.isNotificationsEnabled ? t('popup:emailUnsubscribeSubtitle') : t('popup:emailSubscribeSubtitle')}
        </Text>
      </ConfirmModal>
    </Box>
  );
}

export default GeneralInformation;
